import { Event } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Plus, Minus } from "lucide-react";

interface VIPSeatingTemplateProps {
  event: Event;
  vipTicketCount: number;
  standingTickets: number;
  earlyBirdTickets: number;
  onVipTicketChange: (increment: boolean) => void;
  onStandingTicketChange: (increment: boolean) => void;
  onEarlyBirdTicketChange: (increment: boolean) => void;
}

export const VIPSeatingTemplate = ({
  event,
  vipTicketCount,
  standingTickets,
  earlyBirdTickets,
  onVipTicketChange,
  onStandingTicketChange,
  onEarlyBirdTicketChange,
}: VIPSeatingTemplateProps) => {
  console.log("VIP Template Props:", {
    event,
    vipTicketCount,
    standingTickets,
    earlyBirdTickets,
    maxVipTickets: event.quantityVip,
    maxStandingTickets: event.quantityStanding,
    maxEarlyBirdTickets: event.quantityEarlyBird
  });

  return (
    <div className="space-y-8">
      <div className="grid gap-6">
        {/* VIP Section */}
        <div className="bg-gray-700 p-6 rounded-lg">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center">
              <div>
                <h3 className="text-2xl font-bold text-white">VIP: Meet and Greet</h3>
                {event.ticketDescriptionVip && (
                  <p className="text-gray-400 italic mt-1">{event.ticketDescriptionVip}</p>
                )}
              </div>
              <div className="flex items-center gap-4 mt-4 md:mt-0">
                <span className="text-2xl font-bold text-white">${parseFloat(event.priceVip)}</span>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onVipTicketChange(false)}
                    disabled={vipTicketCount === 0}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="text-2xl font-bold text-white min-w-[3ch]">
                    {vipTicketCount}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onVipTicketChange(true)}
                    disabled={vipTicketCount >= (event.quantityVip || 0)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            {event.quantityVip === 0 && (
              <p className="text-sm text-red-400">Sold Out</p>
            )}
          </div>
        </div>

        {/* Early Bird */}
        {event.earlyBirdPrice && parseFloat(event.earlyBirdPrice) > 0 && (
          <div className="bg-gray-700 p-6 rounded-lg">
            <div className="flex flex-col space-y-4">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white">Early Bird</h3>
                  {event.ticketDescriptionEarlyBird && (
                    <p className="text-gray-400 italic mt-1">{event.ticketDescriptionEarlyBird}</p>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-4 md:mt-0">
                  <span className="text-2xl font-bold text-white">${parseFloat(event.earlyBirdPrice)}</span>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => onEarlyBirdTicketChange(false)}
                      disabled={earlyBirdTickets === 0}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="text-2xl font-bold text-white min-w-[3ch]">
                      {earlyBirdTickets}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => onEarlyBirdTicketChange(true)}
                      disabled={!event.quantityEarlyBird || earlyBirdTickets >= event.quantityEarlyBird}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              {event.quantityEarlyBird === 0 && (
                <p className="text-sm text-red-400">Sold Out</p>
              )}
            </div>
          </div>
        )}

        {/* General Admission */}
        <div className="bg-gray-700 p-6 rounded-lg">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center">
              <div>
                <h3 className="text-2xl font-bold text-white">General Admission</h3>
                {event.ticketDescriptionGeneral && (
                  <p className="text-gray-400 italic mt-1">{event.ticketDescriptionGeneral}</p>
                )}
              </div>
              <div className="flex items-center gap-4 mt-4 md:mt-0">
                <span className="text-2xl font-bold text-white">${parseFloat(event.priceGeneralAdmission)}</span>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onStandingTicketChange(false)}
                    disabled={standingTickets === 0}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="text-2xl font-bold text-white min-w-[3ch]">
                    {standingTickets}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onStandingTicketChange(true)}
                    disabled={!event.quantityStanding || standingTickets >= event.quantityStanding}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            {event.quantityStanding === 0 && (
              <p className="text-sm text-red-400">Sold Out</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};